'use client';
import React from 'react';
import { ContentListCardComponent } from './ContentListCard';
import { CardListing } from 'src/.generated/CardListing/ContentListCard.model';
import {
  ContentListWithOverrides,
  DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
  getContentListWithOverrides,
} from 'lib/utils/components/content-list-data';
import { SearchRecord } from 'lib/services/models/search-response';
import { usePlainTextWrapper } from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { useRichTextWrapper } from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { useImageWrapper } from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { ContentOverride } from 'components/authorable/shared/card-listing/CardListingShared';
import { ComponentParams, GetComponentServerProps, Item } from '@sitecore-content-sdk/nextjs';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';

export type ContentListCardParams = ComponentParams & {
  gridAspectRatio?: Item | string;
  showLabel?: string | undefined;
};

type ContentListCardProps = Omit<CardListing.ContentListCard.ContentListCard, 'params'> & {
  staticProps?: ContentListWithOverrides | null;
  params?: ContentListCardParams;
};

export default function ContentListCard(props: ContentListCardProps) {
  const { fields, staticProps, params } = props;
  const contentImage = useImageWrapper({ field: fields?.contentImage });
  const imageLabel = usePlainTextWrapper({ field: fields?.imageLabel });
  const eyebrow = usePlainTextWrapper({ field: fields?.eyebrow });
  const title = usePlainTextWrapper({ field: fields?.contentTitle });
  const description = useRichTextWrapper({ field: fields?.description });
  const headline = usePlainTextWrapper({ field: fields?.headline });
  const showNumberedList = fields?.showNumberedList?.value;
  const results: SearchRecord[] = staticProps?.result?.Results ?? [];
  const contentOverrides: Record<string, ContentOverride> = staticProps?.contentOverrides ?? {};
  const gridParam = params?.gridAspectRatio;
  const normalizedGridRatio: ImageAspectRatio | undefined =
    typeof gridParam === 'object'
      ? (getEnumValue<ImageAspectRatio>(gridParam) ?? undefined)
      : (gridParam as ImageAspectRatio | undefined);
  const showLabel = params?.showLabel === '1';

  if (!fields) {
    return null;
  }

  return (
    <ContentListCardComponent
      contentImage={contentImage}
      imageLabel={imageLabel}
      eyebrow={eyebrow}
      title={title}
      description={description}
      headline={headline}
      showNumberedList={showNumberedList}
      results={results}
      contentOverrides={contentOverrides}
      fallbackImageUrl={staticProps?.fallbackImageUrl}
      gridAspectRatio={normalizedGridRatio as ImageAspectRatio}
      showLabel={showLabel}
    />
  );
}

export const getComponentServerProps: GetComponentServerProps = async (rendering, layoutData) => {
  const gridParam = rendering.params?.gridAspectRatio;
  const normalizedGridRatio: ImageAspectRatio | undefined =
    typeof gridParam === 'object'
      ? (getEnumValue<ImageAspectRatio>(gridParam) ?? undefined)
      : (gridParam as ImageAspectRatio | undefined);

  return {
    staticProps: await getContentListWithOverrides(rendering, layoutData, {
      gridAspectRatio: normalizedGridRatio ?? DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
    }),
  };
};
